import type {
  EditChartRequest,
  EditContentRequest,
  EditFormatRequest,
  EditStructureRequest,
  ExcelExecutionRunner,
  ExcelRunnerResult,
  FormulaTraceRequestItem,
  ReadContentRequestItem,
  ReadExcelRequest,
  ReadFormattingRequestItem,
  TableAnalysisRequestItem,
} from "@carousel-office/agent-runtime";
import {
  EXCEL_BACKEND_URL,
  PYTHON_BACKEND_URL,
  getResolvedPythonBackendUrl,
} from "@carousel-office/shared";

type JsonRecord = Record<string, unknown>;

interface SemanticFontSettingsPayload {
  enabled: boolean;
  applyOnlyToPreviouslyEmptyCells: boolean;
  colors: {
    numeric: string;
    formulaCalculation: string;
    formulaOtherSheet: string;
    formulaOtherWorkbook: string;
    formulaExternalData: string;
  };
}

function toTelemetry(operationCount: number, startedAt: number) {
  return {
    operationCount,
    durationMs: Date.now() - startedAt,
    usedBackendFallback: false,
    timestamp: new Date().toISOString(),
  };
}

function toFailure<TData = unknown>(message: string, startedAt: number): ExcelRunnerResult<TData> {
  return {
    success: false,
    data: {} as TData,
    errors: [message],
    telemetry: toTelemetry(0, startedAt),
  };
}

function toError(error: unknown, startedAt: number, operationCount: number): ExcelRunnerResult {
  return {
    success: false,
    data: {},
    errors: [error instanceof Error ? error.message : String(error)],
    telemetry: toTelemetry(operationCount, startedAt),
  };
}

function toReadLabel(item: unknown, fallback: string): string {
  if (!item || typeof item !== "object") {
    return fallback;
  }
  const value = item as Record<string, unknown>;
  const sheetName = typeof value.sheetName === "string" ? value.sheetName : undefined;
  const targetRange = typeof value.targetRange === "string" ? value.targetRange : undefined;
  const chartName = typeof value.chartName === "string" ? value.chartName : undefined;
  const cellAddress = typeof value.cellAddress === "string" ? value.cellAddress : undefined;
  if (chartName) {
    return `${fallback}(${sheetName || "all"} ${chartName})`;
  }
  if (cellAddress) {
    return `${fallback}(${sheetName || "sheet"}!${cellAddress})`;
  }
  if (targetRange) {
    return `${fallback}(${sheetName || "sheet"}!${targetRange})`;
  }
  return `${fallback}(${sheetName || "sheet"})`;
}

function normalizeLegacyReadRequest(request: ReadExcelRequest): {
  request: ReadExcelRequest;
  warnings: string[];
} {
  const normalized: ReadExcelRequest = { ...request };
  const warnings: string[] = [];
  const raw = request as unknown as {
    listCharts?: Array<Record<string, unknown>>;
  };

  const canonicalReadCharts = request.readCharts ? [...request.readCharts] : [];
  if (Array.isArray(raw.listCharts) && raw.listCharts.length > 0) {
    warnings.push("Legacy listCharts was normalized to readCharts.");
    for (const item of raw.listCharts) {
      const sheetName =
        typeof item?.sheetName === "string"
          ? item.sheetName
          : typeof item?.sheet_name === "string"
            ? item.sheet_name
            : "";
      if (!sheetName) continue;
      canonicalReadCharts.push({ sheetName, dataMode: "auto" });
    }
  }
  if (canonicalReadCharts.length > 0) {
    normalized.readCharts = canonicalReadCharts;
  }
  return { request: normalized, warnings };
}

export class BackendSessionExcelRunner implements ExcelExecutionRunner {
  readonly runnerType = "backend-session" as const;
  private cachedSemanticFontSettings: SemanticFontSettingsPayload | null = null;
  private semanticFontSettingsFetchedAt = 0;

  constructor(private readonly apiBaseUrl: string = EXCEL_BACKEND_URL) {}

  private async post(path: string, payload: JsonRecord): Promise<JsonRecord> {
    const response = await fetch(`${this.apiBaseUrl}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`${path} failed (${response.status}): ${await response.text()}`);
    }
    return (await response.json()) as JsonRecord;
  }

  private ensureSessionId(options: { sessionId?: string } | undefined): string | undefined {
    return options?.sessionId;
  }

  private normalizeHexColor(value: unknown, fallback: string): string {
    if (typeof value !== "string") {
      return fallback;
    }
    const raw = value.trim();
    const candidate = raw.startsWith("#") ? raw.slice(1) : raw;
    if (!/^[0-9A-Fa-f]{6}$/.test(candidate)) {
      return fallback;
    }
    return `#${candidate.toUpperCase()}`;
  }

  private async getSemanticFontSettings(): Promise<SemanticFontSettingsPayload | null> {
    const now = Date.now();
    // Avoid re-fetching on every call while keeping settings reasonably fresh.
    if (this.cachedSemanticFontSettings && now - this.semanticFontSettingsFetchedAt < 30000) {
      return this.cachedSemanticFontSettings;
    }

    try {
      const pythonBaseUrl = getResolvedPythonBackendUrl(PYTHON_BACKEND_URL).replace(/\/+$/, "");
      const response = await fetch(`${pythonBaseUrl}/api/settings/excel-colors`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        throw new Error(`excel-colors request failed (${response.status})`);
      }
      const body = (await response.json()) as {
        excelColors?: Record<string, unknown>;
      };
      const colors = body.excelColors || {};
      const settings: SemanticFontSettingsPayload = {
        enabled: Boolean(colors.isExcelSemanticFormattingEnabled ?? true),
        applyOnlyToPreviouslyEmptyCells: Boolean(colors.excelApplyColorsOnlyToEmptyCells ?? true),
        colors: {
          numeric: this.normalizeHexColor(colors.excelHardcodedFontColor, "#0000FF"),
          formulaCalculation: this.normalizeHexColor(colors.excelCalculationFontColor, "#000000"),
          formulaOtherSheet: this.normalizeHexColor(
            colors.excelReferenceOtherSheetFontColor,
            "#1E914E"
          ),
          formulaOtherWorkbook: this.normalizeHexColor(
            colors.excelReferenceOtherWorkbookFontColor,
            "#8B0000"
          ),
          formulaExternalData: this.normalizeHexColor(
            colors.excelReferenceExternalDataFontColor,
            "#E30000"
          ),
        },
      };
      this.cachedSemanticFontSettings = settings;
      this.semanticFontSettingsFetchedAt = now;
      return settings;
    } catch (error) {
      console.warn(
        "[BackendSessionExcelRunner] Could not fetch excel semantic color settings:",
        error
      );
      return this.cachedSemanticFontSettings;
    }
  }

  private async runReadBucket<TItem>(
    sessionId: string,
    items: TItem[],
    options: {
      endpoint: string;
      mapPayload: (item: TItem) => JsonRecord;
      label: string;
    }
  ): Promise<{ results: JsonRecord[]; errors: string[] }> {
    const results: JsonRecord[] = [];
    const errors: string[] = [];
    for (const item of items) {
      try {
        results.push(
          await this.post(options.endpoint, {
            session_id: sessionId,
            ...options.mapPayload(item),
          })
        );
      } catch (error) {
        errors.push(
          `${toReadLabel(item, options.label)}: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }
    return { results, errors };
  }

  private async runEditEndpoint(
    endpoint: string,
    payload: JsonRecord,
    startedAt: number,
    operationCount: number
  ): Promise<ExcelRunnerResult> {
    try {
      const data = await this.post(endpoint, payload);
      return {
        success: data.success !== false,
        data,
        telemetry: toTelemetry(operationCount, startedAt),
      };
    } catch (error) {
      return toError(error, startedAt, operationCount);
    }
  }

  async read(
    request: ReadExcelRequest,
    options?: { sessionId?: string }
  ): Promise<ExcelRunnerResult> {
    const startedAt = Date.now();
    const sessionId = this.ensureSessionId(options);
    if (!sessionId) {
      return toFailure("No sessionId provided for backend-session runner.", startedAt);
    }
    const normalizedRequest = normalizeLegacyReadRequest(request);

    const data: JsonRecord = {};
    const errors: string[] = [];
    const warnings = [...normalizedRequest.warnings];
    let operationCount = 0;

    if (normalizedRequest.request.listSheets?.length) {
      operationCount += normalizedRequest.request.listSheets.length;
      const { results, errors: bucketErrors } = await this.runReadBucket(
        sessionId,
        normalizedRequest.request.listSheets,
        {
          endpoint: "/excel/read/sheets",
          mapPayload: () => ({}),
          label: "listSheets",
        }
      );
      data.listSheets = results;
      errors.push(...bucketErrors);
    }

    if (normalizedRequest.request.readContent?.length) {
      operationCount += normalizedRequest.request.readContent.length;
      const { results, errors: bucketErrors } = await this.runReadBucket<ReadContentRequestItem>(
        sessionId,
        normalizedRequest.request.readContent,
        {
          endpoint: "/excel/read/content",
          mapPayload: (item) => ({
            sheet_name: item.sheetName,
            target_range: item.targetRange ?? null,
            mode: item.mode,
          }),
          label: "readContent",
        }
      );
      data.readContent = results;
      errors.push(...bucketErrors);
    }

    if (normalizedRequest.request.readFormatting?.length) {
      operationCount += normalizedRequest.request.readFormatting.length;
      const { results, errors: bucketErrors } = await this.runReadBucket<ReadFormattingRequestItem>(
        sessionId,
        normalizedRequest.request.readFormatting,
        {
          endpoint: "/excel/read/formatting",
          mapPayload: (item) => ({
            sheet_name: item.sheetName,
            target_range: item.targetRange,
            include_content: item.includeContent ?? true,
          }),
          label: "readFormatting",
        }
      );
      data.readFormatting = results;
      errors.push(...bucketErrors);
    }

    if (normalizedRequest.request.readCharts?.length) {
      const results: JsonRecord[] = [];
      const bucketErrors: string[] = [];

      for (const item of normalizedRequest.request.readCharts) {
        if (!item.sheetName) {
          bucketErrors.push("readCharts: sheetName is required.");
          continue;
        }

        try {
          const readSingleChart = async (chartId?: string, chartName?: string) => {
            const chartRead = await this.post("/excel/read-chart", {
              session_id: sessionId,
              sheet_name: item.sheetName,
              chart_id: chartId,
              chart_name: chartName,
              data_mode: item.dataMode ?? "auto",
            });
            results.push(chartRead);
            operationCount += 1;
          };

          if (item.chartId || item.chartName) {
            await readSingleChart(item.chartId, item.chartName);
            continue;
          }

          const listResponse = await this.post("/excel/list-charts", {
            session_id: sessionId,
            sheet_name: item.sheetName,
          });
          operationCount += 1;

          const chartItems = Array.isArray(listResponse.data)
            ? (listResponse.data as Array<Record<string, unknown>>)
            : [];

          for (const chartItem of chartItems) {
            const chartId = typeof chartItem.chartId === "string" ? chartItem.chartId : undefined;
            const chartName =
              typeof chartItem.chartName === "string" ? chartItem.chartName : undefined;

            if (!chartId && !chartName) {
              continue;
            }
            await readSingleChart(chartId, chartName);
          }
        } catch (error) {
          bucketErrors.push(
            `${toReadLabel(item, "readCharts")}: ${
              error instanceof Error ? error.message : String(error)
            }`
          );
        }
      }

      data.readCharts = results;
      errors.push(...bucketErrors);
    }

    if (normalizedRequest.request.formulaTrace?.length) {
      operationCount += normalizedRequest.request.formulaTrace.length;
      const { results, errors: bucketErrors } = await this.runReadBucket<FormulaTraceRequestItem>(
        sessionId,
        normalizedRequest.request.formulaTrace,
        {
          endpoint: "/excel/read/formula-trace",
          mapPayload: (item) => ({
            sheet_name: item.sheetName,
            cell_address: item.cellAddress,
            direction: item.direction ?? "both",
            recursive: item.recursive ?? true,
            max_depth: item.maxDepth ?? 10,
          }),
          label: "formulaTrace",
        }
      );
      data.formulaTrace = results;
      errors.push(...bucketErrors);
    }

    if (normalizedRequest.request.tableAnalysis?.length) {
      operationCount += normalizedRequest.request.tableAnalysis.length;
      const { results, errors: bucketErrors } = await this.runReadBucket<TableAnalysisRequestItem>(
        sessionId,
        normalizedRequest.request.tableAnalysis,
        {
          endpoint: "/excel/read/table-analysis",
          mapPayload: (item) => ({
            sheet_name: item.sheetName,
            target_range: item.targetRange,
            group_by: item.groupBy ?? "column",
            value_threshold: item.valueThreshold ?? 0.05,
            include_empty_analysis: item.includeEmptyAnalysis ?? true,
          }),
          label: "tableAnalysis",
        }
      );
      data.tableAnalysis = results;
      errors.push(...bucketErrors);
    }

    return {
      success: errors.length === 0,
      data,
      warnings: warnings.length ? warnings : undefined,
      errors: errors.length ? errors : undefined,
      telemetry: toTelemetry(operationCount, startedAt),
    };
  }

  async editContent(
    request: EditContentRequest,
    options?: { sessionId?: string }
  ): Promise<ExcelRunnerResult> {
    const startedAt = Date.now();
    const sessionId = this.ensureSessionId(options);
    if (!sessionId) {
      return toFailure("No sessionId provided for backend-session runner.", startedAt);
    }
    const semanticFontSettings = await this.getSemanticFontSettings();
    return this.runEditEndpoint(
      "/excel/edit/content",
      {
        session_id: sessionId,
        sheet_name: request.sheetName,
        edits: request.edits,
        number_formats: request.numberFormats,
        copy_formatting: (request as unknown as JsonRecord).copyFormatting,
        semantic_font_settings: semanticFontSettings || undefined,
      },
      startedAt,
      request.edits?.length || 1
    );
  }

  async editFormat(
    request: EditFormatRequest,
    options?: { sessionId?: string }
  ): Promise<ExcelRunnerResult> {
    const startedAt = Date.now();
    const sessionId = this.ensureSessionId(options);
    if (!sessionId) {
      return toFailure("No sessionId provided for backend-session runner.", startedAt);
    }
    return this.runEditEndpoint(
      "/excel/edit/format",
      {
        session_id: sessionId,
        target_range: request.targetRange,
        sheet_name: request.sheetName,
        column_widths: request.columnWidths,
        row_heights: request.rowHeights,
        custom_range_formatting: request.customRangeFormatting,
        conditional_formatting: request.conditionalFormatting,
        clear_conditional_formatting: request.clearConditionalFormatting,
        copy_formatting: request.copyFormatting,
      },
      startedAt,
      1
    );
  }

  async editChart(
    request: EditChartRequest,
    options?: { sessionId?: string }
  ): Promise<ExcelRunnerResult> {
    const startedAt = Date.now();
    const sessionId = this.ensureSessionId(options);
    if (!sessionId) {
      return toFailure("No sessionId provided for backend-session runner.", startedAt);
    }
    if (request.action === "createWaterfall") {
      return toFailure(
        "createWaterfall is only supported in the Office.js chart runtime. " +
          "For backend-session runs, use action='create' with chartXml.",
        startedAt
      );
    }
    return this.runEditEndpoint(
      "/excel/edit-chart",
      {
        session_id: sessionId,
        sheet_name: request.sheetName,
        action: request.action,
        chart_id: request.chartId,
        chart_name: request.chartName,
        chart_xml: request.chartXml,
        changes: request.changes,
        read_id: request.readId,
      },
      startedAt,
      1
    );
  }

  async editStructure(
    request: EditStructureRequest,
    options?: { sessionId?: string }
  ): Promise<ExcelRunnerResult> {
    const startedAt = Date.now();
    const sessionId = this.ensureSessionId(options);
    if (!sessionId) {
      return toFailure("No sessionId provided for backend-session runner.", startedAt);
    }
    return this.runEditEndpoint(
      "/excel/edit/structure",
      {
        session_id: sessionId,
        operations: request.operations,
      },
      startedAt,
      request.operations?.length || 1
    );
  }
}
